import React, { useEffect, useState } from 'react';
import { X, User, Mail, Phone, Briefcase, Loader2 } from 'lucide-react';
import { CrmContact, contactsApi } from '../services/contactsApi';
import { toast } from 'sonner';
interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact?: CrmContact | null; // If provided, we're editing; otherwise creating
  onSuccess?: () => void;
}
export function ContactModal({
  isOpen,
  onClose,
  contact,
  onSuccess
}: ContactModalProps) {
  const isEditing = !!contact;
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    First_Name: '',
    Last_Name: '',
    Email: '',
    Phone: '',
    Mobile: '',
    Title: '',
    Department: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Reset form when modal opens/closes or contact changes
  useEffect(() => {
    if (isOpen) {
      if (contact) {
        setFormData({
          First_Name: contact.First_Name || '',
          Last_Name: contact.Last_Name || '',
          Email: contact.Email || '',
          Phone: contact.Phone || '',
          Mobile: contact.Mobile || '',
          Title: contact.Title || '',
          Department: contact.Department || ''
        });
      } else {
        setFormData({
          First_Name: '',
          Last_Name: '',
          Email: '',
          Phone: '',
          Mobile: '',
          Title: '',
          Department: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, contact]);
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.Last_Name.trim()) {
      newErrors.Last_Name = 'Last Name is required';
    }
    if (formData.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) {
      newErrors.Email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      if (isEditing && contact) {
        await contactsApi.update(contact.id, formData);
        toast.success('Contact updated', {
          description: `${formData.First_Name} ${formData.Last_Name} has been updated in Zoho CRM.`
        });
      } else {
        await contactsApi.create(formData);
        toast.success('Contact created', {
          description: `${formData.First_Name} ${formData.Last_Name} has been added to Zoho CRM.`
        });
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to save contact:', err);
      const msg = err instanceof Error ? err.message : 'Failed to save contact.';
      toast.error(isEditing ? 'Failed to update contact' : 'Failed to create contact', {
        description: msg
      });
      setErrors(prev => ({
        ...prev,
        submit: msg
      }));
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-1604" data-mp="30hd7ky3iogu6">
      <div className="flex min-h-screen items-center justify-center p-4" data-id="element-1605" data-mp="2vitm0ysu572c">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} data-id="element-1606" data-mp="3af149tkonyez" />

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-id="element-1607" data-mp="35f6btll9f0rt">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0" data-id="element-1608" data-mp="3jdmjpfuuyxbj">
            <div className="flex items-center gap-3" data-id="element-1609" data-mp="3t5itvpa7ydw4">
              <div className="p-2 bg-blue-50 rounded-lg" data-id="element-1610" data-mp="3t594o3x00arh">
                <User className="h-5 w-5 text-blue-600" data-id="element-1611" data-mp="3o65y2f1ddwhe" />
              </div>
              <div data-id="element-1612" data-mp="612oah0gkwan">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-1613" data-mp="60kurs56kw6f">
                  {isEditing ? 'Edit Contact' : 'Add New Contact'}
                </h2>
                <p className="text-sm text-gray-500" data-id="element-1614" data-mp="1xefxw405woc">
                  {isEditing ? 'Update contact information in Zoho CRM' : 'Create a new contact in Zoho CRM'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 flex-shrink-0" data-id="element-1615" data-mp="2592n19usmsw5">
              <X className="h-5 w-5" data-id="element-1616" data-mp="2a970ss4bh9io" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto" data-id="element-1617" data-mp="1clgl0617rwbe">
            <div className="px-6 py-4 space-y-6" data-id="element-1618" data-mp="1hiysrt3hw5ht">
              {errors.submit && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm" data-id="element-1619" data-mp="1mgqluqfvlxnl">
                  {errors.submit}
                </div>}

              {/* Basic Info */}
              <div className="space-y-4" data-id="element-1620" data-mp="2xw5eji11y3cw">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-1621" data-mp="31y0gmoqt4krq">
                  <Mail className="h-4 w-4 text-gray-400" data-id="element-1622" data-mp="qax9a93spcf8" />
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1623" data-mp="m8tkyjw12dcj">
                  <div data-id="element-1624" data-mp="mbpk2bjm382k">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1625" data-mp="hi3fl6nlqahu">
                      First Name
                    </label>
                    <input type="text" value={formData.First_Name} onChange={e => setFormData(prev => ({
                    ...prev,
                    First_Name: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="John" data-id="element-1626" data-mp="cj0iq9hrzaky" />
                  </div>

                  <div data-id="element-1627" data-mp="1nyfghfht66h4">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1628" data-mp="1o0092zkkbfcc">
                      Last Name <span className="text-red-500" data-id="element-1629" data-mp="272ip669ybl2d">*</span>
                    </label>
                    <input type="text" value={formData.Last_Name} onChange={e => setFormData(prev => ({
                    ...prev,
                    Last_Name: e.target.value
                  }))} className={`block w-full rounded-md shadow-sm sm:text-sm ${errors.Last_Name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`} placeholder="Doe" data-id="element-1630" data-mp="2bv2ajpevrkhq" />
                    {errors.Last_Name && <p className="mt-1 text-sm text-red-600" data-id="element-1631" data-mp="15e85zyvom80i">
                        {errors.Last_Name}
                      </p>}
                  </div>

                  <div className="sm:col-span-2" data-id="element-1632" data-mp="1aaotqryyykd2">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1633" data-mp="12sh9260ej6o7">
                      Email Address
                    </label>
                    <input type="email" value={formData.Email} onChange={e => setFormData(prev => ({
                    ...prev,
                    Email: e.target.value
                  }))} className={`block w-full rounded-md shadow-sm sm:text-sm ${errors.Email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`} placeholder="contact@example.com" data-id="element-1634" data-mp="xws2vjtvmrix" />
                    {errors.Email && <p className="mt-1 text-sm text-red-600" data-id="element-1635" data-mp="dgxs2p2jvx4">
                        {errors.Email}
                      </p>}
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div className="space-y-4" data-id="element-1636" data-mp="3b0urvngrbuy8">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-1637" data-mp="3b1mqz89kcat9">
                  <Phone className="h-4 w-4 text-gray-400" data-id="element-1638" data-mp="3fzfvzxz4qyry" />
                  Contact Details
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1639" data-mp="3l0lpvz4j0s32">
                  <div data-id="element-1640" data-mp="3pxc6cnk8tpp0">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1641" data-mp="3py52yvphmytf">
                      Phone
                    </label>
                    <input type="text" value={formData.Phone} onChange={e => setFormData(prev => ({
                    ...prev,
                    Phone: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="+1 234 567 8900" data-id="element-1642" data-mp="2lkx70f75s0h" />
                  </div>

                  <div data-id="element-1643" data-mp="1x08a1qw8lvwx">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1644" data-mp="1s2ghqvrok7s7">
                      Mobile
                    </label>
                    <input type="text" value={formData.Mobile} onChange={e => setFormData(prev => ({
                    ...prev,
                    Mobile: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="+1 234 567 8900" data-id="element-1645" data-mp="1n4exi7iiq2f5" />
                  </div>
                </div>
              </div>

              {/* Professional Info */}
              <div className="space-y-4" data-id="element-1646" data-mp="2yp25aydh734w">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-1647" data-mp="2a5hb8kzoiw4b">
                  <Briefcase className="h-4 w-4 text-gray-400" data-id="element-1648" data-mp="265fhcy06r6zl" />
                  Professional Information
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1649" data-mp="2k600xwdxd6u5">
                  <div data-id="element-1650" data-mp="2f52h7fd9o4qj">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1651" data-mp="2f3r4gq1ufmna">
                      Title
                    </label>
                    <input type="text" value={formData.Title} onChange={e => setFormData(prev => ({
                    ...prev,
                    Title: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="Manager" data-id="element-1652" data-mp="2p3hxsoeo3vls" />
                  </div>

                  <div data-id="element-1653" data-mp="3rgb0wxm3y8j6">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1654" data-mp="3rdowh6uik956">
                      Department
                    </label>
                    <input type="text" value={formData.Department} onChange={e => setFormData(prev => ({
                    ...prev,
                    Department: e.target.value
                  }))} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="Sales" data-id="element-1655" data-mp="3hiep03lcj9k0" />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0" data-id="element-1656" data-mp="2ov110uzmloc1">
              <button type="button" onClick={onClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap disabled:opacity-50" data-id="element-1657" data-mp="2an5vej8eochl">
                Cancel
              </button>
              <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap disabled:opacity-50 flex items-center gap-2" data-id="element-1658" data-mp="1w0cpu5ooul2l">
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-1659" data-mp="2jm9ehn7korw3" />}
                {isEditing ? 'Save Changes' : 'Create Contact'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}